import React, { useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import "./InteractiveEsignMap.css";

// World topojson
const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Color mapping for statuses
const STATUS_COLORS = {
  same_status: "#18A999", // teal
  enforceable_not_equal: "#FF7A53", // orange-red
  unclear_prefer_digital: "#F5B63A", // yellow
  no_data: "#444444", // dark grey
};

const DEFAULT_FILL = "#D9D9D9"; // non-highlighted

// ⚠️ SAMPLE DATA – replace text + status with your real data
// key MUST match geo.properties.NAME from topojson
const COUNTRY_INFO = {
  Canada: {
    label: "Canada",
    statusKey: "same_status",
    legalityStatus: "Same status as written signature",
    legalDescription:
      "Electronic signatures have been recognized by Canadian law since 1999 under the Uniform Electronic Commerce Act and related legislation.",
    yearRecognized: "1999",
    keyRestrictions:
      "Some real estate and estate-related documents may be excluded.",
    courtAdmissible: "Y",
    businessUse: "Y",
    legalModel: "Open",
  },
  "United States of America": {
    label: "United States",
    statusKey: "same_status",
    legalityStatus: "Same status as written signature",
    legalDescription:
      "eSignatures are recognized under ESIGN Act and UETA for most interstate and intrastate commerce.",
    yearRecognized: "2000",
    keyRestrictions:
      "Certain family law, wills, and court filings can require wet signatures.",
    courtAdmissible: "Y",
    businessUse: "Y",
    legalModel: "Open",
  },
  Australia: {
    label: "Australia",
    statusKey: "same_status",
    legalityStatus: "Same status as written signature",
    legalDescription:
      "Recognized under the Electronic Transactions Act and related state legislation.",
    yearRecognized: "1999",
    keyRestrictions: "Some documents (like wills) may have extra formalities.",
    courtAdmissible: "Y",
    businessUse: "Y",
    legalModel: "Open",
  },
  "Russian Federation": {
    label: "Russia",
    statusKey: "enforceable_not_equal",
    legalityStatus:
      "Enforceable but not always equivalent to handwritten signature",
    legalDescription:
      "eSignatures recognized under Federal Law No. 63-FZ 'On Electronic Signature'.",
    yearRecognized: "2011",
    keyRestrictions:
      "Different levels of signatures (simple, enhanced) with different evidentiary value.",
    courtAdmissible: "Y",
    businessUse: "Y",
    legalModel: "Tiered",
  },
  Malaysia: {
    label: "Malaysia",
    statusKey: "unclear_prefer_digital",
    legalityStatus:
      "Status may be unclear or strong preference for digital certificates",
    legalDescription:
      "Electronic signatures regulated by the Digital Signature Act and Electronic Commerce Act.",
    yearRecognized: "1997",
    keyRestrictions:
      "Some transactions may require licensed certification authority signatures.",
    courtAdmissible: "Y",
    businessUse: "Y",
    legalModel: "Tiered",
  },
  "Democratic Republic of the Congo": {
    label: "DR Congo",
    statusKey: "no_data",
    legalityStatus: "No reliable data available",
    legalDescription:
      "There is limited or no clear publicly available legislation on electronic signatures.",
    yearRecognized: "—",
    keyRestrictions: "No data.",
    courtAdmissible: "N/A",
    businessUse: "N/A",
    legalModel: "N/A",
  },
  // 👉 Add the rest of your countries here using the same structure
};

const InteractiveEsignMap = () => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  const [zoom, setZoom] = useState(1.2);

  const handleMove = (evt, geo) => {
    const name = geo.properties.NAME;
    const info = COUNTRY_INFO[name];

    if (!info) {
      setTooltip((prev) => ({ ...prev, visible: false }));
      return;
    }

    setTooltip({
      visible: true,
      x: evt.clientX + 12,
      y: evt.clientY + 12,
      data: info,
    });
  };

  const handleLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.4, 4));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.4, 0.8));

  const getFillColor = (geo) => {
    const info = COUNTRY_INFO[geo.properties.NAME];
    if (!info) return DEFAULT_FILL;
    return STATUS_COLORS[info.statusKey] || DEFAULT_FILL;
  };

  return (
    <div className="emap-wrapper">
      <h2 className="emap-title">Countries Where eSignatures Are Legal</h2>

      <div className="emap-map-shell">
        {/* Zoom buttons */}
        <div className="emap-zoom-controls">
          <button onClick={zoomIn}>+</button>
          <button onClick={zoomOut}>−</button>
        </div>

        <ComposableMap
          projectionConfig={{ scale: 150 }}
          className="emap-map"
        >
          <ZoomableGroup zoom={zoom}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseMove={(evt) => handleMove(evt, geo)}
                    onMouseLeave={handleLeave}
                    style={{
                      default: {
                        fill: getFillColor(geo),
                        outline: "none",
                        stroke: "#ffffff",
                        strokeWidth: 0.5,
                      },
                      hover: {
                        fill: getFillColor(geo),
                        outline: "none",
                        stroke: "#111827",
                        strokeWidth: 0.8,
                      },
                      pressed: {
                        fill: getFillColor(geo),
                        outline: "none",
                      },
                    }}
                  />
                ))
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip.visible && tooltip.data && (
          <div
            className="emap-tooltip"
            style={{ top: tooltip.y, left: tooltip.x }}
          >
            <h3 className="emap-tooltip-title">{tooltip.data.label}</h3>
            <p className="emap-tooltip-status">
              {tooltip.data.legalityStatus}
            </p>
            <p className="emap-tooltip-line">
              <strong>Legality: </strong>
              {tooltip.data.legalDescription}
            </p>
            <p className="emap-tooltip-line">
              <strong>Year: </strong>
              {tooltip.data.yearRecognized}
            </p>
            <p className="emap-tooltip-line">
              <strong>Key Restrictions: </strong>
              {tooltip.data.keyRestrictions}
            </p>
            <p className="emap-tooltip-line">
              <strong>Court-Admissible: </strong>
              {tooltip.data.courtAdmissible}
            </p>
            <p className="emap-tooltip-line">
              <strong>General Business Use: </strong>
              {tooltip.data.businessUse}
            </p>
            <p className="emap-tooltip-line">
              <strong>E-signature Legal Model: </strong>
              {tooltip.data.legalModel}
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="emap-legend">
        <div className="emap-legend-item">
          <span
            className="emap-legend-swatch"
            style={{ backgroundColor: STATUS_COLORS.same_status }}
          />
          <span>
            Countries where electronic signatures have the same legal status as
            written signatures
          </span>
        </div>
        <div className="emap-legend-item">
          <span
            className="emap-legend-swatch"
            style={{ backgroundColor: STATUS_COLORS.enforceable_not_equal }}
          />
          <span>
            Countries where eSignatures are enforceable but not fully equivalent
            to handwritten signatures
          </span>
        </div>
        <div className="emap-legend-item">
          <span
            className="emap-legend-swatch"
            style={{ backgroundColor: STATUS_COLORS.unclear_prefer_digital }}
          />
          <span>
            Countries where the status is unclear or digital certificates are
            strongly preferred
          </span>
        </div>
        <div className="emap-legend-item">
          <span
            className="emap-legend-swatch"
            style={{ backgroundColor: STATUS_COLORS.no_data }}
          />
          <span>Countries with no clear electronic signature data</span>
        </div>
      </div>
    </div>
  );
};

export default InteractiveEsignMap;
